import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { MOTIVO_MAX_LENGTH, MOTIVO_MIN_LENGTH } from './schemas';
import type {
  CompanyNotaFiscalCancelarInput,
  CompanyNotaFiscalDto,
  CompanyNotaFiscalListItem,
} from './types';

/**
 * Motivos frequentes — atalho pra preencher o textarea; usuário pode editar
 * livremente depois. Traduzidos via i18n em `cancelar.motivosFrequentes.*`.
 */
const MOTIVOS_FREQUENTES = [
  'erroDigitacao',
  'valorIncorreto',
  'clienteIncorreto',
  'canceladoPeloCliente',
  'duplicidade',
] as const;

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

interface CancelarVars {
  id: string;
  input: CompanyNotaFiscalCancelarInput;
  /** Obrigatório na lista de todas as empresas; na lista de uma emitente o hook ignora. */
  empresaId?: string;
}

export interface CompanyNotaFiscalCancelarDialogProps {
  open: boolean;
  nota: CompanyNotaFiscalListItem | null;
  useCancelar: () => UseMutationResult<CompanyNotaFiscalDto, Error, CancelarVars>;
  onClose: () => void;
}

/**
 * Dialog de cancelamento de NF Empresa. Toda NF cancelada libera o saldo da RC
 * vinculada **obrigatoriamente** (regra de negócio confirmada 2026-07-20) —
 * não há mais toggle. O front chama só `/cancelar`; a liberação do saldo é
 * responsabilidade do proc upstream. Pede `motivo` (≥10 chars).
 */
export function CompanyNotaFiscalCancelarDialog({
  open,
  nota,
  useCancelar,
  onClose,
}: CompanyNotaFiscalCancelarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const cancelar = useCancelar();
  const [motivo, setMotivo] = useState('');
  const [touched, setTouched] = useState(false);

  function resetState(): void {
    setMotivo('');
    setTouched(false);
  }

  const motivoTrim = motivo.trim();
  const motivoError = touched && motivoTrim.length < MOTIVO_MIN_LENGTH;
  const isPending = cancelar.isPending;

  async function handleSubmit(): Promise<void> {
    if (!nota) return;
    setTouched(true);
    if (motivoTrim.length < MOTIVO_MIN_LENGTH) return;
    const vars: CancelarVars = {
      id: nota.id,
      empresaId: nota.empresaId,
      input: { motivo: motivoTrim },
    };
    try {
      await cancelar.mutateAsync(vars);
      toast.success(t('features.empresa.notasFiscais.toasts.cancelada'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: resetState }}
    >
      <DialogTitle>{t('features.empresa.notasFiscais.cancelar.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {nota && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: 4,
                borderLeftColor: 'error.main',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {t('features.empresa.notasFiscais.cancelar.evidenciaLabel')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {t('features.empresa.notasFiscais.cancelar.evidenciaNumero', {
                  numero: nota.numero ?? `#${nota.id}`,
                })}
                {' — '}
                {nota.clienteNome ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatBRL(nota.valor)}
                {nota.requisicaoCompraReferencia ? ` · RC ${nota.requisicaoCompraReferencia}` : ''}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {t('features.empresa.notasFiscais.cancelar.motivosFrequentesLabel')}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {MOTIVOS_FREQUENTES.map((slug) => {
                const label = t(`features.empresa.notasFiscais.cancelar.motivosFrequentes.${slug}`);
                return (
                  <Chip
                    key={slug}
                    size="small"
                    label={label}
                    variant="outlined"
                    onClick={() => {
                      setMotivo(label.slice(0, MOTIVO_MAX_LENGTH));
                      setTouched(true);
                    }}
                    disabled={isPending}
                  />
                );
              })}
            </Stack>
          </Box>

          <TextField
            label={t('features.empresa.notasFiscais.cancelar.motivoLabel')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value.slice(0, MOTIVO_MAX_LENGTH))}
            onBlur={() => setTouched(true)}
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            error={motivoError}
            helperText={
              motivoError
                ? t('features.empresa.notasFiscais.cancelar.motivoRequired', {
                    min: MOTIVO_MIN_LENGTH,
                  })
                : t('features.empresa.notasFiscais.cancelar.motivoHelper', {
                    used: motivoTrim.length,
                    min: MOTIVO_MIN_LENGTH,
                    max: MOTIVO_MAX_LENGTH,
                  })
            }
          />

          {nota?.requisicaoCompraReferencia && (
            <Alert severity="info" variant="outlined">
              {t('features.empresa.notasFiscais.cancelar.liberaSaldoInfo', {
                rc: nota.requisicaoCompraReferencia,
              })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleSubmit()}
          disabled={isPending}
        >
          {isPending ? t('common.saving') : t('features.empresa.notasFiscais.cancelar.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
